import { ContactPhoneCallLogModal } from "@/components/features/contact/ContactPhoneCallLogModal";
import { ViewMatterEmailSlideover } from "@/components/features/matter/emails/ViewMatterEmailSlideover";
import { AddOrEditExpenseModal } from "@/components/features/matter/expenses/AddOrEditExpenseModal";
import { AddOrEditFileModal } from "@/components/features/matter/file-repo/AddOrEditFileModal";
import { GenerateDocumentSlideover } from "@/components/features/matter/file-repo/GenerateDocument/GenerateDocumentSlideover";
import { MergeDocumentModal } from "@/components/features/matter/file-repo/merge-document/MergeDocumentSlideover";
import { CreateOrEditShareFolderModal } from "@/components/features/matter/file-repo/share-folders/CreateOrEditShareFolderModal";
import { ShareFolderAccessModal } from "@/components/features/matter/file-repo/share-folders/ShareFolderAccessModal";
import { ShareFolderFilePreviewSlideover } from "@/components/features/matter/file-repo/share-folders/ShareFolderFilePreviewSlideover";
import { CreateSignatureRequestSlideover } from "@/components/features/matter/file-repo/signature-requests/CreateSignatureRequestSlideover";
import { UpdateFirmSignatureRequestSlideover } from "@/components/features/matter/file-repo/signature-requests/UpdateFirmSignatureRequestSlideover";
import { UpdateSignatureRequestSlideover } from "@/components/features/matter/file-repo/signature-requests/UpdateSignatureRequestSlideover";
import { AddOrEditIncidentReportModal } from "@/components/features/matter/incident/AddOrEditIncidentReportModal";
import { CaseIncidentDescriptionFormModal } from "@/components/features/matter/incident/CaseIncidentDescriptionFormModal/CaseIncidentDescriptionFormModal";
import { CaseInjuriesFormModal } from "@/components/features/matter/incident/CaseInjuriesFormModal/CaseInjuriesFormModal";
import { IntakeResponseModal } from "@/components/features/matter/information/IntakeResponseModal";
import { MatterCriticalInfoSlideover } from "@/components/features/matter/information/MatterCriticalInfoSlideover";
import { AddOrEditInsuranceModal } from "@/components/features/matter/insurance/AddOrEditInsuranceModal/AddOrEditInsuranceModal";
import { AddOrEditMatterContactConnectionSlideover } from "@/components/features/matter/layout/AddOrEditMatterContactConnectionSlideover/AddOrEditMatterContactConnectionSlideover";
import { AddOrEditLienModal } from "@/components/features/matter/liens/AddOrEditLienModal";
import { AddOrEditMatterLitigationDateModal } from "@/components/features/matter/litigation/AddOrEditMatterLitigationDateModal";
import { MatterLitigationDatePlanConnectionModal } from "@/components/features/matter/litigation/MatterLitigationDatePlanConnectionModal";
import { AddOrEditLostWageModal } from "@/components/features/matter/lost-wages/AddOrEditLostWageModal";
import { AddOrEditMedicalTreatmentSlideover } from "@/components/features/matter/medical-treatments/AddOrEditMedicalTreatmentSlideover";
import { AddOrEditTreatmentAppointmentSlideover } from "@/components/features/matter/medical-treatments/AddOrEditTreatmentAppointmentSlideover/AddOrEditTreatmentAppointmentSlideover";
import { AddOrEditTreatmentBalanceConfirmationModal } from "@/components/features/matter/medical-treatments/AddOrEditTreatmentBalanceConfirmationModal/AddOrEditTreatmentBalanceConfirmationModal";
import { AddOrEditTreatmentBillSlideover } from "@/components/features/matter/medical-treatments/AddOrEditTreatmentBillSlideover/AddOrEditTreatmentBillSlideover";
import { AddOrEditTreatmentPlanSlideover } from "@/components/features/matter/medical-treatments/AddOrEditTreatmentPlanSlideover/AddOrEditTreatmentPlanSlideover";
import { AddOrEditTreatmentRecordSlideover } from "@/components/features/matter/medical-treatments/AddOrEditTreatmentRecordSlideover/AddOrEditTreatmentRecordSlideover";
import { AssociateTreatmentAppointmentsSlideover } from "@/components/features/matter/medical-treatments/AssociateTreatmentAppointmentsSlideover/AssociateTreatmentAppointmentsSlideover";
import { BalanceConfirmationHistoryModal } from "@/components/features/matter/medical-treatments/BalanceConfirmationHistoryModal/BalanceConfirmationHistoryModal";
import { AddOrEditMatterNoteModal } from "@/components/features/matter/notes/AddOrEditMatterNoteModal/AddOrEditMatterNoteModal";
import { AddOrEditPropertyDamageSlideover } from "@/components/features/matter/property-damage/AddOrEditPropertyDamageSlideover";
import { AddOrEditSettlementCounterModal } from "@/components/features/matter/settlements/AddOrEditSettlementCounterModal";
import { AddOrEditSettlementDemandModal } from "@/components/features/matter/settlements/AddOrEditSettlementDemandModal";
import { AddOrEditMatterTaskModal } from "@/components/features/matter/tasks/AddOrEditMatterTaskModal/AddOrEditMatterTaskModal";
import { AddOrEditTrustCheckModal } from "@/components/features/matter/trust-checks/AddOrEditTrustCheckModal";
import { AddOrEditVehicleModal } from "@/components/features/matter/vehicles/AddOrEditVehicleModal/AddOrEditVehicleModal";
import { CommunicationSlideover } from "@/components/ui/CommunicationSlideover/CommunicationSlideover";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { AddRelatedMatterModal } from "@/components/ui/RelatedMattersSlideover/AddRelatedMatterModal";
import { SendEmailSlideover } from "@/components/ui/SendEmailSlideover/SendEmailSlideover";
import { SendMatterEmailSlideover } from "@/components/ui/SendMatterEmailSlideover/SendMatterEmailSlideover";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { useModals, type ActiveSlideover } from "@/providers/ModalProvider";
import { useQuery } from "@tanstack/react-query";
import React from "react";

export const GlobalModalManager: React.FC = () => {
  const { activeSlideovers, closeSlideover } = useModals();
  const { hasActionPermission } = useUser();

  const hasEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );

  const renderModal = (slideover: ActiveSlideover) => {
    const { id, payload, isOpen } = slideover;
    const setOpen = (open: boolean) => {
      if (!open) {
        closeSlideover(id);
      }
    };

    switch (payload.type) {
      case "communication":
        return (
          <CommunicationSlideover
            key={`${id}-communication`}
            open={isOpen}
            setOpen={setOpen}
            selectedContactId={payload.contactId}
            matterId={payload.matterId}
            defaultMessage={payload.defaultMessage}
          />
        );
      case "phone-call-log":
        return (
          <ContactPhoneCallLogModal
            key={`${id}-phone-call-log`}
            open={isOpen}
            setOpen={setOpen}
            contactId={payload.contactId}
          />
        );
      case "send-email":
        return (
          <SendEmailSlideover
            key={`${id}-send-email`}
            open={isOpen}
            setOpen={setOpen}
            title={payload.title}
            description={payload.description}
            toEmails={payload.toEmails}
            contactIds={payload.contactIds}
            templateName={payload.templateName}
            templateData={payload.templateData}
            defaultCcEmails={payload.defaultCcEmails}
            defaultBccEmails={payload.defaultBccEmails}
            onSuccess={payload.onSuccess}
          />
        );
      case "send-matter-email":
        return (
          <SendMatterEmailSlideover
            key={`${id}-send-matter-email`}
            open={isOpen}
            setOpen={setOpen}
            title={payload.title}
            description={payload.description}
            matterId={payload.matterId}
            contactId={payload.contactId}
            toEmails={payload.toEmails}
            templateName={payload.templateName}
            templateData={payload.templateData}
            matterFiles={payload.matterFiles}
            onSuccess={payload.onSuccess}
          />
        );
      case "matter-email-view":
        return (
          <ViewMatterEmailSlideover
            key={`${id}-matter-email-view`}
            open={isOpen}
            setOpen={setOpen}
            title={payload.title}
            description={payload.description}
            matterId={payload.matterId}
            matterEmailId={payload.matterEmailId}
          />
        );
      case "vehicle":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditVehicleModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing ?? false}
              matterId={payload.matterId}
              vehicleId={payload.vehicleId}
              setSelectedVehicleId={payload.setSelectedVehicleId}
              title={payload.title}
              description={payload.description}
              enforceRequiredContact={payload.enforceRequiredContact}
              requiredVehiclePartyContactId={
                payload.requiredVehiclePartyContactId
              }
              initialData={payload.initialData}
              defendantContactIdContext={payload.defendantContactIdContext}
              vehiclePartyRole={payload.vehiclePartyRole}
              sourceMatterContactConnectionId={
                payload.sourceMatterContactConnectionId
              }
            />
          </MatterSlideoverWrapper>
        );

      case "insurance":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditInsuranceModal
              key={id}
              description={payload.description}
              editing={payload.editing ?? false}
              insuranceId={payload.insuranceId}
              matterId={payload.matterId}
              open={isOpen}
              setOpen={setOpen}
              title={payload.title}
              afterCreateFn={payload.afterCreateFn}
              enforceRequiredContact={payload.enforceRequiredContact}
              requiredInsurancePartyContactId={
                payload.requiredInsurancePartyContactId
              }
              initialData={payload.initialData}
              defendantContactIdContext={payload.defendantContactIdContext}
              insurancePartyRole={payload.insurancePartyRole}
              sourceMatterContactConnectionId={
                payload.sourceMatterContactConnectionId
              }
            />
          </MatterSlideoverWrapper>
        );

      case "property-damage":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditPropertyDamageSlideover
              key={id}
              title={payload.title}
              description={payload.description}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing ?? false}
              initialTab={payload.initialTab}
              matterId={payload.matterId}
              propertyDamageId={payload.propertyDamageId}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "incident-report":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditIncidentReportModal
              key={id}
              title={payload.title}
              description={payload.description}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing ?? false}
              matterId={payload.matterId}
              incidentReportId={payload.incidentReportId}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "matter-task":
        return (
          <AddOrEditMatterTaskModal
            key={id}
            activeMatterTaskId={payload.taskId}
            description={payload.description}
            editing={payload.editing}
            hasDeletePermission={hasDeletePermission}
            hasEditPermission={hasEditPermission}
            hideMatterField={payload.hideMatterField}
            matterId={payload.matterId}
            massTortId={payload.massTortId}
            modalStackId={id}
            open={isOpen}
            setOpen={setOpen}
            subItem={payload.subItem}
            title={payload.title}
            initialData={payload.initialData}
          />
        );

      case "lien":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditLienModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              matterId={payload.matterId}
              lienId={payload.lienId}
              title={payload.title}
              description={payload.description}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "lost-wage":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditLostWageModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              matterId={payload.matterId}
              lostWageId={payload.lostWageId}
              title={payload.title}
              description={payload.description}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "matter-litigation-date":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditMatterLitigationDateModal
              key={id}
              title={payload.title}
              description={payload.description}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              matterId={payload.matterId}
              litigationDateId={payload.litigationDateId}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "matter-litigation-date-plan-connection":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <MatterLitigationDatePlanConnectionModal
              key={id}
              title={payload.title}
              description={payload.description}
              open={isOpen}
              setOpen={setOpen}
              matterId={payload.matterId}
            />
          </MatterSlideoverWrapper>
        );

      case "matter-note":
        return payload.matterId ? (
          <AddOrEditMatterNoteModal
            key={id}
            open={isOpen}
            setOpen={setOpen}
            editing={payload.editing}
            matterId={payload.matterId}
            noteId={payload.noteId}
            title={payload.title}
            description={payload.description}
            subItem={payload.subItem}
            initialData={payload.initialData}
          />
        ) : null;

      case "settlement-demand":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditSettlementDemandModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              matterId={payload.matterId}
              settlementId={payload.settlementId}
              negotiationType={payload.negotiationType}
              selectedInsurance={payload.selectedInsurance}
              title={payload.title}
              description={payload.description}
              isMedpay={payload.isMedpay}
              coverageType={payload.coverageType}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );
      case "settlement-counter":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditSettlementCounterModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              matterId={payload.matterId}
              settlementCounterId={payload.settlementCounterId}
              selectedInsurance={payload.selectedInsurance}
              title={payload.title}
              description={payload.description}
              coverageType={payload.coverageType}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "expense":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditExpenseModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              initialTab={payload.initialTab}
              matterId={payload.matterId}
              expenseId={payload.expenseId}
              title={payload.title}
              description={payload.description}
              initialData={payload.initialData}
              splitMode={payload.splitMode}
            />
          </MatterSlideoverWrapper>
        );

      case "medical-treatment":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditMedicalTreatmentSlideover
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              matterId={payload.matterId}
              treatmentId={payload.treatmentId}
              title={payload.title}
              description={payload.description}
              initialData={payload.initialData}
              initialProvider={payload.initialProvider}
            />
          </MatterSlideoverWrapper>
        );

      case "treatment-appointment":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditTreatmentAppointmentSlideover
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              initialTab={payload.initialTab}
              matterId={payload.matterId}
              appointmentId={payload.appointmentId}
              treatmentId={payload.treatmentId}
              title={payload.title}
              description={payload.description}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );
      case "associate-treatment-appointments":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AssociateTreatmentAppointmentsSlideover
              key={id}
              open={isOpen}
              setOpen={setOpen}
              matterId={payload.matterId}
              treatmentId={payload.treatmentId}
              appointmentIds={payload.appointmentIds}
              title={payload.title}
              description={payload.description}
              onSuccess={payload.onSuccess}
            />
          </MatterSlideoverWrapper>
        );

      case "treatment-bill":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditTreatmentBillSlideover
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              initialTab={payload.initialTab}
              matterId={payload.matterId}
              billId={payload.billId}
              treatmentId={payload.treatmentId}
              afterCreateFn={payload.afterCreateFn}
              title={payload.title}
              description={payload.description}
              creatingFromAppointment={payload.creatingFromAppointment}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "treatment-balance-confirmation":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditTreatmentBalanceConfirmationModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              matterId={payload.matterId}
              treatmentId={payload.treatmentId}
              title={payload.title}
              description={payload.description}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "treatment-balance-confirmation-history":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <BalanceConfirmationHistoryModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              title={payload.title}
              description={payload.description}
              matterId={payload.matterId}
              treatmentId={payload.treatmentId}
            />
          </MatterSlideoverWrapper>
        );

      case "treatment-record":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditTreatmentRecordSlideover
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              initialTab={payload.initialTab}
              matterId={payload.matterId}
              recordId={payload.recordId}
              treatmentId={payload.treatmentId}
              afterCreateFn={payload.afterCreateFn}
              title={payload.title}
              description={payload.description}
              creatingFromAppointment={payload.creatingFromAppointment}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "treatment-plan":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditTreatmentPlanSlideover
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing}
              initialTab={payload.initialTab}
              matterId={payload.matterId}
              appointmentId={payload.appointmentId}
              planId={payload.planId}
              treatmentId={payload.treatmentId}
              title={payload.title}
              description={payload.description}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "file":
        return payload.matterId ? (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditFileModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              editing={payload.editing ?? false}
              activeFileId={payload.activeFileId}
              currentFolderId={payload.currentFolderId}
              isFolder={payload.isFolder}
              handleDeleteItem={payload.handleDeleteItem}
              matterId={payload.matterId}
              isCriticalImage={payload.isCriticalImage}
              autoOpenUpload={payload.autoOpenUpload}
              initialFile={payload.initialFile}
              afterCreateFile={payload.afterCreateFile}
              showPreview={payload.showPreview}
            />
          </MatterSlideoverWrapper>
        ) : (
          <AddOrEditFileModal
            key={id}
            open={isOpen}
            setOpen={setOpen}
            editing={payload.editing ?? false}
            activeFileId={payload.activeFileId}
            currentFolderId={payload.currentFolderId}
            isFolder={payload.isFolder}
            handleDeleteItem={payload.handleDeleteItem}
            massTortId={payload.massTortId}
            isCriticalImage={payload.isCriticalImage}
            autoOpenUpload={payload.autoOpenUpload}
            initialFile={payload.initialFile}
            afterCreateFile={payload.afterCreateFile}
            showPreview={payload.showPreview}
          />
        );

      case "share-folder":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <CreateOrEditShareFolderModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              matterId={payload.matterId}
              title={payload.title}
              description={payload.description}
              initialSelectedFileIds={payload.initialSelectedFileIds}
              editing={payload.editing}
              shareFolderId={payload.shareFolderId}
            />
          </MatterSlideoverWrapper>
        );

      case "share-folder-file":
        return (
          <ShareFolderFilePreviewSlideover
            key={id}
            open={isOpen}
            setOpen={setOpen}
            title={payload.title}
            description={payload.description}
            fileName={payload.matterFileName}
            presignedUrlKey={payload.matterShareFolderFilePresignedUrlKey}
            shareFolderLabel={payload.shareFolderLabel}
          />
        );

      case "share-folder-access":
        return (
          <ShareFolderAccessModal
            key={id}
            open={isOpen}
            setOpen={setOpen}
            matterId={payload.matterId}
            shareFolderId={payload.shareFolderId}
            title={payload.title}
            description={payload.description}
          />
        );

      case "generate-document":
        return (
          <GenerateDocumentSlideover
            key={id}
            open={isOpen}
            setOpen={setOpen}
            matter={payload.matter}
            afterGenerateFn={payload.afterGenerateFn}
            matterFileIdLocation={payload.matterFileIdLocation}
            templateType={payload.templateType}
            restrictTemplatesToType={payload.restrictTemplatesToType}
            hideSaveAndMerge={payload.hideSaveAndMerge}
            subItem={payload.subItem}
          />
        );

      case "merge-document":
        return (
          <MergeDocumentModal
            key={id}
            open={isOpen}
            setOpen={setOpen}
            matterId={"matterId" in payload ? payload.matterId : undefined}
            massTortId={
              "massTortId" in payload ? payload.massTortId : undefined
            }
            initialSelectedFileIds={payload.initialSelectedFileIds}
          />
        );

      case "signature-request":
        return (
          <UpdateSignatureRequestSlideover
            key={id}
            open={isOpen}
            setOpen={setOpen}
            matterId={payload.matterId}
            title={payload.title}
            description={payload.description ?? ""}
            signatureRequestId={payload.signatureRequestId}
            presentation={payload.presentation}
          />
        );

      case "firm-signature-request":
        return (
          <UpdateFirmSignatureRequestSlideover
            key={id}
            open={isOpen}
            setOpen={setOpen}
            title={payload.title}
            description={payload.description ?? ""}
            reportRow={payload.reportRow}
          />
        );

      case "create-signature-request":
        return (
          <CreateSignatureRequestSlideover
            key={id}
            open={isOpen}
            setOpen={setOpen}
            matterId={payload.matterId}
            subItem={payload.subItem}
            title={payload.title}
            description={payload.description ?? ""}
          />
        );

      case "add-related-matter":
        return (
          <AddRelatedMatterModal
            key={id}
            matterId={payload.matterId}
            open={isOpen}
            setOpen={setOpen}
          />
        );

      case "intake-response":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <IntakeResponseModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              matterId={payload.matterId}
            />
          </MatterSlideoverWrapper>
        );

      case "matter-trust-check":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditTrustCheckModal
              key={id}
              open={isOpen}
              setOpen={setOpen}
              matterId={payload.matterId}
              trustCheckId={payload.trustCheckId}
              title={payload.title}
              description={payload.description}
              editing={payload.editing}
              trustCheckType={payload.trustCheckType}
              settlementOfferId={payload.settlementOfferId}
              settlementDemandId={payload.settlementDemandId}
              settlementCounterId={payload.settlementCounterId}
              expenseId={payload.expenseId}
              lienId={payload.lienId}
              medicalTreatmentBillId={payload.medicalTreatmentBillId}
              initialData={payload.initialData}
            />
          </MatterSlideoverWrapper>
        );

      case "case-incident-description":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <CaseIncidentDescriptionFormModal
              key={id}
              isOpen={isOpen}
              setIsOpen={setOpen}
              matterId={payload.matterId}
            />
          </MatterSlideoverWrapper>
        );

      case "case-injuries":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <CaseInjuriesFormModal
              key={id}
              isOpen={isOpen}
              setIsOpen={setOpen}
              matterId={payload.matterId}
            />
          </MatterSlideoverWrapper>
        );

      case "matter-critical-info":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <MatterCriticalInfoSlideover
              key={id}
              isOpen={isOpen}
              setIsOpen={setOpen}
              matterId={payload.matterId}
            />
          </MatterSlideoverWrapper>
        );

      case "matter-contact-connection":
        return (
          <MatterSlideoverWrapper key={id} matterId={payload.matterId}>
            <AddOrEditMatterContactConnectionSlideover
              key={id}
              isOpen={isOpen}
              setIsOpen={setOpen}
              disableDelete={payload.disableDelete}
              previewContact={payload.previewContact}
              defaultCreateContactValues={payload.defaultCreateContactValues}
              defaultContactSearchObject={payload.defaultContactSearchObject}
              matterContactConnection={payload.matterContactConnection}
              matterId={payload.matterId}
              labelOptions={payload.labelOptions ?? []}
              disableTime={payload.disableTime}
              autoOpenContactSelection={payload.autoOpenContactSelection}
              selectContactOnly={payload.selectContactOnly}
              excludeContactIds={payload.excludeContactIds}
              reservedContactLabelPairs={payload.reservedContactLabelPairs}
              editPresentation={payload.editPresentation}
              createPresentation={payload.createPresentation}
              title={payload.title}
              description={payload.description}
            />
          </MatterSlideoverWrapper>
        );

      default:
        console.warn(`Unknown modal type: ${payload.type}`);
        return null;
    }
  };

  return <>{activeSlideovers.map(renderModal)}</>;
};

// Separate component to handle data fetching for matter-scoped modals/slideovers
const MatterSlideoverWrapper: React.FC<{
  matterId: string;
  children: React.ReactNode;
}> = ({ matterId, children }) => {
  const {
    data: matterData,
    isLoading,
    error,
  } = useQuery(retrieveMatterQueryOptions(matterId));

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !matterData) {
    return (
      <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Error loading matter data</p>
        </div>
      </div>
    );
  }

  return (
    <>{React.cloneElement(children as React.ReactElement, { matterData })}</>
  );
};
